import React from 'react';
import styled, { css } from 'styled-components';
import { colorTokens, semanticColors } from '@/common/styles/colorTokens';
import { sizes } from '@/common/styles/sizes';
import { typography } from '@/common/styles/typographyTokens';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $variant?:'gradient' | 'fill' | 'outline' | 'ghost' | 'destructive' | 'outline-destructive';
  $fillColor?: string;
  $textColor?: string;
  $borderColor?: string;
  $size?: 'lg' | 'md' | 'sm' | 'xs' | 'xxs';
  $isFull?: boolean;
  $isDisabled?: boolean;
}

const getVariantStyles = (variant: ButtonProps['$variant'] = 'fill') => {
  switch (variant) {
    case 'gradient':
      return css`
        background: linear-gradient(to right, ${colorTokens.brand[900]}, ${colorTokens.brand[600]});
        color: ${semanticColors.text.white};
        &:hover { 
          opacity: 0.8;
        }
      `;
    case 'fill':
      return css`
        background-color: ${semanticColors.background.brand};
        color: ${semanticColors.text.white};
        &:hover {
          background-color: ${semanticColors.background.brand}80;
        }
      `;
    case 'outline':
      return css`
        border: 1px solid ${semanticColors.border.tertiary};
        background-color: ${semanticColors.background.secondary};
        color: ${semanticColors.text.secondary};
        &:hover {
          background-color: ${semanticColors.background.secondaryHover};
          border-color: ${semanticColors.border.tertiary}80;
        }
      `;
    case 'ghost':
      return css`
        background-color: transparent;
        color: ${semanticColors.text.secondary};
        &:hover {
          background-color: ${semanticColors.background.primaryHover}80;
        }
      `;
    case 'destructive':
      return css`
        background-color: ${semanticColors.background.danger.primary};
        color: ${semanticColors.text.white};
        &:hover {
          background-color: ${semanticColors.background.danger.primaryHover}80;
        }
      `;
    case 'outline-destructive':
      return css`
        background-color: ${semanticColors.background.secondary};
        border: 1px solid ${semanticColors.border.dangerPrimary};
        color: ${semanticColors.text.onDanger.secondary};
        &:hover {
          background-color: ${semanticColors.text.onDanger.tertiary}80;
        }
      `;
    default:
      return '';
  }
};

const getSizeStyles = (size: ButtonProps['$size'] = 'md') => {
  switch (size) {
  
    case 'lg':
      return css`
        min-height: 52px;
        max-height: 52px;
        min-width: 180px;
        padding: ${sizes.padding[16]} ${sizes.padding[24]};
        ${typography.button.button1}
      `;
    case 'md':
      return css`
        min-height: 48px;
        max-height: 48px;
        min-width: 120px;
        padding: ${sizes.padding[16]} ${sizes.padding[24]};
        ${typography.button.button3}
      `;
    case 'sm':
      return css`
        min-height: 40px;
        max-height: 40px;
        min-width: 80px;
        padding: ${sizes.padding[8]} ${sizes.padding[24]};
        ${typography.button.button4}
      `;
    case 'xs':
      return css`
        min-height: 36px;
        max-height: 36px;
        min-width: 57px;
        padding: ${sizes.padding[8]} ${sizes.padding[16]};
        ${typography.button.button5}
      `;
    case 'xxs':
      return css`
        width: fit-content;
        min-height: 24px;
        max-height: 24px;
        padding: ${sizes.padding[8]} ${sizes.padding[8]};
        ${typography.button.button5}
      `;
    default:
      return '';
  }
};

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: ${sizes.radius.lg};
  transition: all 0.2s;
  border: none;
  outline: none;
  cursor: pointer !important;

  &:focus-visible {
    outline: 2px solid hsl(var(--ring));
    outline-offset: 2px;
  }

  &:disabled {
    background: ${semanticColors.background.disabled};
    color: ${semanticColors.text.disabled};
    pointer-events: none;
  }

  ${props => props.$isDisabled && css`
    background: ${semanticColors.background.disabled};
    color: ${semanticColors.text.disabled};
  `}

  ${props => !props.$isDisabled && getVariantStyles(props.$variant)}
  ${props => getSizeStyles(props.$size)}

  ${props => props.$isFull && css`
    width: 100%;
  `}

  ${props => props.$fillColor && css`
    background-color: ${props.$fillColor};
  `}
  ${props => props.$textColor && css`
    color: ${props.$textColor};
  `}
  ${props => props.$borderColor && css`
    border: 1px solid ${props.$borderColor};
  `}
`;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, $variant = 'fill', $isFull = false, $size = 'md', $fillColor, $textColor,  $borderColor , $isDisabled, ...props }, ref) => {
    return (
      <StyledButton ref={ref} $variant={$variant} $isFull={$isFull} $size={$size} $fillColor={$fillColor} $textColor={$textColor} $borderColor={$borderColor} {...props} $isDisabled={$isDisabled}>
        {children}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export { Button };

